import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Euro, Calendar, Zap, Flame, Wifi, CheckCircle2, Clock,
  TrendingUp, User, FileText,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const fmtEuro = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);

/**
 * Vista provvigioni per il sub-agente.
 * NON mostra il listino provvigionale, ma solo cio' che ha maturato
 * mese per mese: contratti + cliente + compagnia + importo.
 */
const MieProvvigioni = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/provvigioni/mie-mensili`, {
        params: { year, month },
      });
      setData(res.data);
    } catch (e) {
      setData({ contratti: [], totale_mese: 0, n_contratti: 0 });
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) years.push(y);

  const totalePagato = (data?.contratti || []).reduce((s, c) => s + (c.commissione_pagata ? c.importo_maturato : 0), 0);
  const totalePending = (data?.totale_mese || 0) - totalePagato;

  const iconForTipo = (t) => t === 'gas' ? Flame : t === 'internet' ? Wifi : Zap;

  return (
    <div className="space-y-6" data-testid="mie-provvigioni-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Le Mie Provvigioni</h1>
          <p className="text-sm text-slate-500">Quanto hai maturato mese per mese</p>
        </div>
        <div className="flex gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
            <SelectTrigger className="w-40" data-testid="filter-mese"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MESI.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-24" data-testid="filter-anno"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="kpi-totale-mese">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-sky-100 flex items-center justify-center">
              <Euro className="w-6 h-6 text-sky-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Maturato {MESI[month - 1]} {year}</p>
              <p className="text-2xl font-bold text-slate-900 tabular-nums">
                {loading ? <Skeleton className="h-8 w-24" /> : fmtEuro(data?.totale_mese)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-pending">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Da incassare</p>
              <p className="text-2xl font-bold text-slate-900 tabular-nums">
                {loading ? <Skeleton className="h-8 w-24" /> : fmtEuro(totalePending)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-pagato">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Gia' incassato</p>
              <p className="text-2xl font-bold text-slate-900 tabular-nums">
                {loading ? <Skeleton className="h-8 w-24" /> : fmtEuro(totalePagato)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="w-5 h-5 text-sky-500" />
            Contratti inseriti nel mese
            <Badge variant="outline" className="ml-2">{data?.n_contratti || 0}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
          ) : (data?.contratti || []).length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">Nessun contratto inserito in {MESI[month - 1]} {year}</p>
              <p className="text-xs mt-1">Inserisci una nuova pratica per iniziare a maturare provvigioni</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.contratti.map(c => {
                const Icon = iconForTipo(c.tipologia);
                return (
                  <div
                    key={c.contract_id}
                    className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl hover:border-sky-300 transition-colors"
                    data-testid={`row-provv-${c.contract_id}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-slate-900 truncate">
                          <User className="w-3 h-3 inline mr-1 text-slate-400" />
                          {c.cliente_nome}
                        </span>
                        {c.codice_pratica && (
                          <span className="font-mono text-[10px] bg-sky-50 text-sky-700 px-1.5 py-0.5 rounded">
                            {c.codice_pratica}
                          </span>
                        )}
                        <Badge variant="outline" className="text-[10px]">
                          {(c.tipologia || '').toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-x-3 gap-y-1 mt-1 text-xs text-slate-500 flex-wrap">
                        <span>{c.compagnia || '—'}</span>
                        {c.offerta && <span className="truncate">• {c.offerta}</span>}
                        {c.metodo_pagamento === 'rid' && <span className="text-emerald-600">RID</span>}
                        {(c.metodo_pagamento === 'bolletta_web' || c.tipo_bolletta === 'web') && <span className="text-sky-600">Bolletta Web</span>}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">
                        <Calendar className="w-2.5 h-2.5 inline mr-0.5" />
                        {c.data_inserimento ? new Date(c.data_inserimento).toLocaleDateString('it-IT') : ''}
                        {c.stato && <span className="ml-2">• stato: <strong>{c.stato}</strong></span>}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-slate-900 tabular-nums">{fmtEuro(c.importo_maturato)}</p>
                      {c.commissione_pagata ? (
                        <Badge className="bg-emerald-500 text-white text-[10px]">Incassato</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 text-[10px] border-amber-200">In attesa</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MieProvvigioni;
